/**
 * @file Host utils for string manipulation.
 */

/**
 * Capitalize the first letter of a string.
 *
 * @param string - String to format
 * @returns String with its first character capitalized
 */
export const capitalize = (string: string) => {
    if (!string) return string

    return `${string[0].toUpperCase()}${string.substring(1)}`
}

/**
 * Get the number of occurences of characters in a string.
 *
 * @param string - String to parse
 * @param characters - Characters to find occurences of
 * @returns Number of occurences
 */
export const getOccurences = (string: string, characters: string) => {
    // TODO remove type safety check after TS migration done
    if (!string || typeof string !== "string") return 0

    return string.split(characters).length - 1
}

/**
 * Limit a text length to a given amount of words.
 *
 * @param text - Text to shorten
 * @param maxWords - Number of words that serves as a limit
 * @returns Shortened text
 */
export const shortenTextByWords = (text: string, maxWords = 40) => {
    const nearEndWords = 4

    if (!text) {
        return text
    }

    const wordsWithPunctuation = text.split(" ")

    // Text is just about the allowed size, tolerate as it is
    if (wordsWithPunctuation.length - maxWords <= nearEndWords) {
        return text
    }

    const limitedText = wordsWithPunctuation.slice(0, maxWords).join(" ")

    return `${limitedText}...`
}

export const shortenTextByCharacters = (text: string, maxLength = 400) => {
    const nearEndIndex = 20

    if (!text || text.length < maxLength) {
        return text
    }

    const punctuations = [".", ",", "?", "!", ":", ";"] // Three dot ellipsis handled below
    const characters = text.split("")
    const limitedIndex = maxLength - 10
    const lastLimitedCharacter = characters[limitedIndex]
    const nextSpaceIndex = characters.indexOf(" ", limitedIndex)
    const isLimitedCharacterNearEnd =
        nextSpaceIndex === -1 || text.length - nextSpaceIndex <= nearEndIndex

    // Text is just about the allowed size, tolerate as it is
    if (isLimitedCharacterNearEnd) {
        return text
    }

    // TODO handle when limitedCharacter is already part of an ellipsis
    // const isLastLimitedCharacterAlreadyEllipsis =
    //     lastLimitedCharacter === "…"
    //     || (lastLimitedCharacter === "." && (characters[limitedIndex + 1] === "." || characters[limitedIndex - 1] === "."))

    // if (isLastLimitedCharacterAlreadyEllipsis) {
    //     return text.slice(0, ellipsisIndex)
    // }

    const ellipsisIndex = punctuations.includes(lastLimitedCharacter)
        ? nextSpaceIndex + 1
        : nextSpaceIndex
    const limitedText = `${text.slice(0, ellipsisIndex)}...`

    return limitedText
}

/**
 * Remove all accent from a string.
 *
 * @param value - String to parse
 * @returns String without accents
 */
export function removeAccents(value: string) {
    return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
}

/**
 * Replace all special characters from a string by something else.
 *
 * @param input - String to parse
 * @param replacement - String to replace special characters with
 * @returns String with special characters replaced by the replacement string
 */
export function replaceSpecialCharacters(input: string, replacement: string = " ") {
    return input.replace(/[’"'`´\/\\;:.,?!\-–—_()\[\]{}<>|~@#$&]/g, replacement).trim()
}

export function formatStreetNumber(address: string) {
    // Regex to extract the street number from an address
    const match = address.match(/^\s*(\d+[a-zA-Z]*(-\d+[a-zA-Z]*)?\s*(bis|ter|quater|quinquies)?)/i)

    return match ? match[1].trim() : null
}

export function formatStreetName(address: string) {
    const streetNumber = formatStreetNumber(address) ?? ""

    return removeAccents(replaceSpecialCharacters(address.replace(streetNumber, ""))).toUpperCase()
}
